import HomeNavBar from "@/components/HomeNavBar/HomeNavBar.vue";
import { get_token, get_user_details } from "@/router";
import Swal from "sweetalert2";

const ORDER_POLLING_INTERVAL_MS = 10000;

export default {
    name: "MyOrdersView",
    components: {
        HomeNavBar,
    },
    data() {
        return ({
            active_tab: "orders",
            loading: false,
            error_message: "",
            user_details: get_user_details(),
            my_orders: [],
            my_requests: [],
            search_query: "",
            order_filter: "all",
            request_filter: "all",
            is_small_device: false,
            is_filter_panel_open: false,
            orders_polling_interval: null,
            is_fetching_my_orders: false,
            cancelling_order_ids: [],
            
            // Rating system data
            rating_request: null,
            new_rating: 0,
            hover_rating: 0,
            new_review_text: '',
            isSubmittingRating: false,
        });
    },
    computed: {
        orders_tab_count() {
            return this.my_orders.filter((order) => {
                const status = String(order?.order_status || order?.order_status_name || "").trim().toLowerCase();
                return ["pending", "approved"].includes(status);
            }).length;
        },
        requests_tab_count() {
            return this.my_requests.filter((request) => {
                const status = String(request?.status?.name || request?.status_name || request?.status || "").trim().toLowerCase();
                return ["pending", "approved"].includes(status);
            }).length;
        },
        filtered_orders() {
            const query = this.search_query.trim().toLowerCase();

            return this.my_orders.filter((order) => {
                const status = String(order?.order_status || order?.order_status_name || "").trim().toLowerCase();
                const matches_filter = this.match_order_status(order, status, this.order_filter);
                return matches_filter && this.order_search_match(order, query);
            });
        },

        filtered_requests() {
            const query = this.search_query.trim().toLowerCase();

            return this.my_requests.filter((request) => {
                const status = String(request?.status?.name || request?.status_name || request?.status || "").trim().toLowerCase();
                
                // Simplified Filter Logic
                const matches_filter = this.request_filter === "all" || 
                                       (this.request_filter === "completed" 
                                           ? ["completed", "resolved", "confirmed"].includes(status) 
                                           : status === this.request_filter);
                
                // Simplified Query Logic
                const matches_query = !query || this.request_search_match(request).includes(query);

                return matches_filter && matches_query;
            });
        },
        activity_stats() {
            if (this.active_tab === "orders") {
                return [
                    { label: "Total Orders", value: this.my_orders.length, icon: "bi bi-box-seam" },
                    { label: "Pending", value: this.my_orders.filter(order => ["pending"].includes(String(order?.order_status).trim().toLowerCase())).length, icon: "bi bi-hourglass-split" },
                    { label: "Approved", value: this.my_orders.filter(order => ["approved", "accepted"].includes(String(order?.order_status).trim().toLowerCase())).length, icon: "bi bi-bag-check" },
                    { label: "Credit", value: this.my_orders.filter(order => this.is_credit_order(order)).length, icon: "bi bi-credit-card-2-front" },
                    { label: "Not Approved", value: this.my_orders.filter(order => ["rejected"].includes(String(order?.order_status).trim().toLowerCase())).length, icon: "bi bi-x-circle" },
                    { label: "Completed", value: this.my_orders.filter(order => ["completed", "delivered", "resolved"].includes(String(order?.order_status).trim().toLowerCase())).length, icon: "bi bi-check2-circle" },
                ];
            }

            return [
                { label: "Total", value: this.my_requests.length, icon: "bi bi-wrench-adjustable-circle" },
                { label: "Pending", value: this.my_requests.filter(request => ["pending"].includes(String(request?.status?.name || request?.status).trim().toLowerCase())).length, icon: "bi bi-hourglass-split" },
                { label: "Approved", value: this.my_requests.filter(request => ["approved", "accepted"].includes(String(request?.status?.name || request?.status).trim().toLowerCase())).length, icon: "bi bi-bag-check" },
                { label: "Not Approved", value: this.my_requests.filter(request => ["rejected"].includes(String(request?.status?.name || request?.status).trim().toLowerCase())).length, icon: "bi bi-x-circle" },
                { label: "Completed", value: this.my_requests.filter(request => ["completed", "resolved", "confirmed"].includes(String(request?.status?.name || request?.status).trim().toLowerCase())).length, icon: "bi bi-check2-circle" },
            ];
        },
        filter_options() {
            return this.active_tab === "orders"
                ? [
                    { label: "All", value: "all" },
                    { label: "Pending", value: "pending" },
                    { label: "Approved", value: "approved" },
                    { label: "Credit", value: "credit_orders" },
                    { label: "Not Approved", value: "rejected" },
                    { label: "Completed", value: "completed" }
                ]
                : [
                    { label: "All", value: "all" },
                    { label: "Pending", value: "pending" },
                    { label: "Approved", value: "approved" },
                    { label: "Not Approved", value: "rejected" },
                    { label: "Completed", value: "completed" }
                ];
        },
        rating_level() {
            const levels = {
                1: 'Poor',
                2: 'Fair',
                3: 'Good',
                4: 'Very Good',
                5: 'Excellent'
            };
            return levels[this.new_rating] || 'Select a rating';
        },
        show_filter_panel() {
            return !this.is_small_device || this.is_filter_panel_open;
        }
        
    },
    watch: {
        "$route.query.tab": {
            immediate: true,
            handler(tab) {
                this.active_tab = this.normalize_activity_tab(tab);
            }
        }
    },
    methods: {
        normalize_activity_tab(tab) {
            const normalized_tab = String(tab || "").trim().toLowerCase();

            if (["services", "service", "requests"].includes(normalized_tab)) {
                return "requests";
            }

            return "orders";
        },
        getToken() {
            return get_token();
        },
        is_credit_order(order) {
            return String(order?.payment_type || order?.payment_method || "")
                .trim()
                .toLowerCase()
                .includes("credit");
        },
        match_order_status(order, status, filter) {
            if (filter === "all") return true;
            if (filter === "credit_orders") return this.is_credit_order(order);
            if (filter === "accepted") return ["accepted", "approved"].includes(status);
            if (filter === "rejected") return ["rejected", "cancelled"].includes(status);
            if (filter === "completed") return ["completed", "delivered", "resolved"].includes(status);
            return status === filter;
        },
        get_order_status(order) {
            return String(order?.order_status || order?.order_status_name || "").trim().toLowerCase();
        },
        format_status_label(status) {
            const normalized_status = String(status || "").trim().toLowerCase();
            if (normalized_status === "rejected") return "Not Approved";
            return normalized_status
                .split(/[\s_]+/)
                .filter(Boolean)
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");
        },
        can_cancel_order(order) {
            const status = this.get_order_status(order);
            const blocked_statuses = ["paid", "cancelled", "rejected","credit"];
            return Boolean(order?.id) && !blocked_statuses.includes(status);
        },
        is_cancelling_order(order) {
            return this.cancelling_order_ids.includes(order?.id);
        },
        request_search_match(request) {
            const product_name = request?.product?.name || request?.service?.name || "";
            const companyName = request?.company_name || request?.company?.name || request?.business_name || "";
            return `${product_name} ${companyName}`.toLowerCase();
        },
        order_search_match(order, query) {
            if (!query) return true;
            return this.order_summary_match(order, query) || this.order_items_match(order, query);
        },
        order_summary_match(order, query) {
            const order_number_match = String(order?.order_number || "").toLowerCase().includes(query);
            const business_match = String(order?.company_name || "").toLowerCase().includes(query);
            return order_number_match || business_match;
        },
        order_items_match(order, query) {
            return (order?.products || []).some((item) => {
                const name = item?.product?.name || item?.product_name || "";
                return name.toLowerCase().includes(query);
            });
        },
        get_my_orders(show_loading = true) {
            if (this.is_fetching_my_orders) return;

            this.is_fetching_my_orders = true;
            if (show_loading) {
                this.loading = true;
            }
            this.error_message = "";

            $.ajax({
                type: "POST",
                url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/get_my_orders_and_services/`,
                headers: {
                    'Authorization': `Token ${this.getToken()}`,
                    'Content-Type': 'application/json'
                    },
                success: (response) => {
                    const expanded_orders = new Map(
                        this.my_orders.map((order) => [String(order.id), order.is_expanded])
                    );
                    const request_ui_state = new Map(
                        this.my_requests.map((request) => [
                            String(request.id),
                            {
                                is_expanded: Boolean(request.is_expanded),
                                show_cancel_input: Boolean(request.show_cancel_input),
                                cancel_reason: String(request.cancel_reason || ""),
                            },
                        ])
                    );

                    this.my_orders = (response?.data?.orders || []).map((order) => ({
                        ...order,
                        is_expanded: expanded_orders.get(String(order.id)) || false,
                    }));
                    this.my_requests = (response?.data?.requests || []).map((request) => ({
                        ...request,
                        is_expanded: request_ui_state.get(String(request.id))?.is_expanded || false,
                        show_cancel_input: request_ui_state.get(String(request.id))?.show_cancel_input || false,
                        cancel_reason: request_ui_state.get(String(request.id))?.cancel_reason || "",
                    }));
                },
                error: (xhr) => {
                    this.error_message =
                        xhr?.responseJSON?.message ||
                        "Unable to load your activity right now.";
                    if (show_loading) {
                        this.my_orders = [];
                        this.my_requests = [];
                    }
                },
                complete: () => {
                    if (show_loading) {
                        this.loading = false;
                    }
                    this.is_fetching_my_orders = false;
                },
            });
        },
        start_orders_polling() {
            this.stop_orders_polling();
            this.orders_polling_interval = setInterval(() => {
                this.get_my_orders(false);
            }, ORDER_POLLING_INTERVAL_MS);
        },
        stop_orders_polling() {
            if (!this.orders_polling_interval) return;
            clearInterval(this.orders_polling_interval);
            this.orders_polling_interval = null;
        },
        current_filter() {
            return this.active_tab === "orders" ? this.order_filter : this.request_filter;
        },
        set_filter(value) {
            if (this.active_tab === "orders") {
                this.order_filter = value;
                return;
            }
            this.request_filter = value;
        },
        update_filter_layout() {
            if (typeof window === "undefined") return;

            this.is_small_device = window.innerWidth < 1200;

            if (!this.is_small_device) {
                this.is_filter_panel_open = true;
            }
        },
        toggle_filter_panel() {
            if (!this.is_small_device) return;
            this.is_filter_panel_open = !this.is_filter_panel_open;
        },
        toggle_expand(item) {
            item.is_expanded = !item.is_expanded;
        },
        format_currency(amount) {
            const value = Number(amount || 0);
            return `R${value.toFixed(2)}`;
        },
        get_number_value(...values) {
            const value = values.find((item) => item !== undefined && item !== null && item !== "");
            const number_value = Number(value);

            return Number.isFinite(number_value) ? number_value : 0;
        },
        get_optional_number_value(...values) {
            const value = values.find((item) => item !== undefined && item !== null && item !== "");
            const number_value = Number(value);

            return Number.isFinite(number_value) ? number_value : null;
        },
        get_order_explicit_discount_amount(order) {
            return this.get_number_value(
                order?.order_promos?.[0]?.total_discount_amount,
                order?.promo_discount_amount,
                order?.discount_amount,
                order?.total_discount_amount
            );
        },
        get_order_saved_total(order) {
            return this.get_optional_number_value(
                order?.total_after_discount,
                order?.discounted_total,
                order?.total_after_promos,
                order?.payable_total,
                order?.amount_due
            );
        },
        get_order_subtotal(order) {
            const explicit_subtotal = this.get_optional_number_value(
                order?.subtotal_amount,
                order?.subtotal,
                order?.total_before_discount,
                order?.original_total_amount,
                order?.original_amount,
                order?.total_cost
            );

            if (explicit_subtotal !== null) return explicit_subtotal;

            const saved_total = this.get_order_saved_total(order);
            const discount_amount = this.get_order_explicit_discount_amount(order);

            if (saved_total !== null && discount_amount > 0) {
                return saved_total + discount_amount;
            }

            return this.get_number_value(order?.total_amount);
        },
        get_order_discount_amount(order) {
            const discount_amount = this.get_order_explicit_discount_amount(order);

            if (discount_amount > 0) return discount_amount;

            const saved_total = this.get_order_saved_total(order);
            if (saved_total === null) return 0;

            return Math.max(this.get_order_subtotal(order) - saved_total, 0);
        },
        get_order_payable_total(order) {
            const saved_total = this.get_order_saved_total(order);

            if (saved_total !== null) return saved_total;

            return Math.max(this.get_order_subtotal(order) - this.get_order_discount_amount(order), 0);
        },
        has_order_promo(order) {
            return this.get_order_discount_amount(order) > 0 || Boolean(order?.order_promos?.length);
        },
        format_short_date(date_string) {
            if (!date_string) return "N/A";

            return new Date(date_string).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });
        },
        format_attribute_text(value) {
            return String(value || "")
                .trim()
                .split(/[\s_-]+/)
                .filter(Boolean)
                .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                .join(" ");
        },
        pick_first_attribute_value(...values) {
            return values.find((value) => value !== undefined && value !== null && value !== "") || "";
        },
        get_attribute_label(attribute) {
            return this.pick_first_attribute_value(
                attribute?.attribute_type?.name,
                attribute?.attribute_type,
                attribute?.name
            );
        },
        get_attribute_value(attribute) {
            return this.pick_first_attribute_value(
                attribute?.label,
                attribute?.value?.value,
                attribute?.value?.name,
                attribute?.value,
                attribute?.attribute_value?.value,
                attribute?.attribute_value?.name
            );
        },
        get_item_attributes(item) {
            const rawAttributes =
                item?.attributes ||
                item?.product_attributes ||
                item?.attribute_values ||
                [];

            if (!Array.isArray(rawAttributes) || !rawAttributes.length) {
                return [];
            }

            return rawAttributes
                .map((attribute) => {
                    const rawLabel = this.get_attribute_label(attribute);
                    const rawValue = this.get_attribute_value(attribute);

                    const label = this.format_attribute_text(rawLabel);
                    const value = this.format_attribute_text(rawValue);

                    if (!label || !value) {
                        return null;
                    }

                    return { label, value };
                })
                .filter(Boolean);
        },
        get_order_rejection_reason(order) {
            const status = String(order?.order_status || order?.order_status_name || "")
                .trim()
                .toLowerCase();

            if (!["rejected"].includes(status)) {
                return "";
            }

            return String(
                order?.reason ||
                order?.rejection_reason ||
                order?.reject_reason ||
                order?.decline_reason ||
                order?.return_message ||
                ""
            ).trim();
        },
         go_back() {
            this.$router.back();
        },

        view_order(order) {
            if (!order?.id) return;

            this.$router.push({
                name: "cart_checkout",
                query: { order: order.id },
            });
        },
        cancel_order(order) {
            if (!this.can_cancel_order(order) || this.is_cancelling_order(order)) return;

            Swal.fire({
                title: 'Cancel Order',
                text: 'Are you sure you want to cancel this order?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, cancel order',
                cancelButtonText: 'Keep order',
                position: 'bottom-end',
                toast: true
            }).then((result) => {
                if (!result.isConfirmed) return;

                this.cancelling_order_ids.push(order.id);

                $.ajax({
                    url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/cancel_order/`,
                    method: "POST",
                    headers: {
                        Authorization: `Token ${this.getToken()}`,
                        'Content-Type': 'application/json'
                    },
                    data: JSON.stringify({
                        user_id: this.user_details?.user_id,
                        order_id: order.id
                    }),
                    success: (response) => {
                        if (response?.status && response.status !== "success") {
                            Swal.fire({
                                icon: 'error',
                                title: 'Error',
                                text: response.message || 'Failed to cancel order.',
                                position: 'bottom-end',
                                showConfirmButton: false,
                                timer: 3000,
                                toast: true
                            });
                            return;
                        }

                        order.order_status = response?.data?.order_status || response?.order?.order_status || 'cancelled';
                        order.order_status_name = response?.data?.order_status_name || response?.order?.order_status_name || 'cancelled';
                        order.updated_at = new Date().toISOString();

                        Swal.fire({
                            icon: 'success',
                            title: 'Order Cancelled',
                            text: 'Your order has been cancelled.',
                            showConfirmButton: false,
                            timer: 1500,
                            position: 'bottom-end',
                            toast: true
                        });

                        this.get_my_orders(false);
                    },
                    error: (xhr) => {
                        Swal.fire({
                            icon: 'error',
                            title: 'Error',
                            text: xhr.responseJSON?.message || xhr.responseJSON?.error || 'Failed to cancel order.',
                            position: 'bottom-end',
                            showConfirmButton: false,
                            timer: 3000,
                            toast: true
                        });
                    },
                    complete: () => {
                        this.cancelling_order_ids = this.cancelling_order_ids.filter((id) => id !== order.id);
                    }
                });
            });
        },

        update_service_request_status(request, action, message = "") {
            const payload = {
                request_id: request.id,
                action: action,
                return_message: message || request.cancel_reason || "",
            };

            $.ajax({
                type: "POST",
                url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/update_service_request_status/`,
                headers: {
                    'Authorization': `Token ${this.getToken()}`,
                    'Content-Type': 'application/json'
                },
                data: JSON.stringify(payload),
                success: (response) => {
                    const newStatus = response.data?.status || { id: 4, name: 'confirmed' };
                        request.status = newStatus;
                        request.updated_at = new Date().toISOString();
                    if (response.status === "success") {
                        const action_label = action.charAt(0).toUpperCase() + action.slice(1);
                        const success_text = action === "confirmed"
                            ? "Thank you for confirming. Would you like to rate this service?"
                            : response.message || `Service request ${action} successfully.`;

                        Swal.fire({
                            icon: "success",
                            title: `${action_label}!`,
                            text: success_text,
                            showConfirmButton: false,
                            position: 'bottom-end',
                            timer: 1200,
                            position: 'bottom-end',
                            toast: true
                        }).then(() => {
                            if (action === "confirmed") {
                                this.open_rating_modal(request);
                            }
                            request.show_cancel_input = false;
                        });
                        
                    } else {
                        Swal.fire({
                          title: 'Error',
                          text: response.message || 'Failed to confirm service.',
                          icon: 'error',
                          position: 'bottom-end',
                          showConfirmButton: false,
                          timer: 3000,
                          toast: true
                        });
                    }
                },
                error: (xhr) => {
                    Swal.fire({
                      title: 'Error',
                      text: xhr.responseJSON?.message || 'Failed to update status.',
                      icon: 'error',
                      position: 'bottom-end',
                      showConfirmButton: false,
                      timer: 3000,
                      toast: true
                    });
                }
            });
        },
        prompt_dispute_request(request) {
            Swal.fire({
                title: "Dispute service request",
                input: "textarea",
                inputLabel: "Tell us why you are disputing this service",
                inputPlaceholder: "Enter your dispute message...",
                inputAttributes: {
                    "aria-label": "Enter your dispute message"
                },
                showCancelButton: true,
                confirmButtonText: "Submit dispute",
                cancelButtonText: "Back",
                inputValidator: (value) => {
                    if (!String(value || "").trim()) {
                        return "Please enter a dispute message.";
                    }
                    return null;
                }
            }).then((result) => {
                if (result.isConfirmed) {
                    this.update_service_request_status(request, "disputed", String(result.value || "").trim());
                }
            });
        },
        open_rating_modal(request) {
            this.rating_request = request;
            this.new_rating = 0;
            this.hover_rating = 0;
            this.new_review_text = '';
            $('#ratingModal').modal('show');
        },
        submit_rating() {
            if (!this.new_rating || !this.new_review_text.trim()) return;

            const productId = this.rating_request?.product?.id || this.rating_request?.service?.id;
            if (!productId) {
                Swal.fire({
                  title: 'Error',
                  text: 'Could not identify product or service to rate.',
                  icon: 'error',
                  position: 'bottom-end',
                  showConfirmButton: false,
                  timer: 3000,
                  toast: true
                });
                return;
            }

            this.isSubmittingRating = true;
            const payload = {
                product_id: productId,
                rating: this.new_rating,
                review: this.new_review_text,
            };

            $.ajax({
                url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/create_product_review/`,
                type: 'POST',
                headers: {
                    Authorization: `Token ${this.getToken()}`,
                    'Content-Type': 'application/json',
                },
                data: JSON.stringify(payload),
                success: (response) => {
                    if (response.status === 'success') {
                        Swal.fire({
                            icon: 'success',
                            title: 'Thank you!',
                            text: 'Your review has been submitted.',
                            position: 'bottom-end',
                            timer: 2000,
                            showConfirmButton: false,
                            toast: true
                        });
                        $('#ratingModal').modal('hide');
                    } else {
                        Swal.fire({
                          title: 'Error',
                          text: response.message || 'Failed to submit rating.',
                          icon: 'error',
                          position: 'bottom-end',
                          showConfirmButton: false,
                          timer: 3000,
                          toast: true
                        });
                    }
                },
                error: (xhr) => {
                    Swal.fire({
                      title: 'Error',
                      text: xhr.responseJSON?.message || 'Error submitting rating.',
                      icon: 'error',
                      position: 'bottom-end',
                      showConfirmButton: false,
                      timer: 3000,
                      toast: true
                    });
                },
                complete: () => {
                    this.isSubmittingRating = false;
                }
            });
        },

        format_money(value) {
            if (!value) return '0';
            return Number(value).toLocaleString('en-ZA', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        }
    },
    mounted() {
        this.update_filter_layout();
        window.addEventListener("resize", this.update_filter_layout);
        this.get_my_orders();
        this.start_orders_polling();
    },
    beforeUnmount() {
        window.removeEventListener("resize", this.update_filter_layout);
        this.stop_orders_polling();
    },
};
